<template>
  <div class="former-field">
    <div class="former-field-container">
      <label
        class="former-field-label"
        :for="field.id"
      >
        {{ label }}<span
          v-if="required"
          class="former-field-required"
        >*</span>
      </label>

      <div :class="classes">
        <div
          :is="type"
          ref="input"
          v-model="value"
          :disabled="field.processing"
          :field="field"
          class="former-field-element"
          @input="input"
          @submit="submit"
          @resize="resized"
        />

        <transition
          mode="out-in"
          name="out-in"
        >
          <former-submit
            ref="submit"
            :key="button.label"
            :class="submitClasses"
            :processing="field.processing"
            v-bind="button"
            @submit="submit"
            @keydown.enter.prevent="submit"
          />
        </transition>
      </div>

      <div
        v-if="field.errors.length"
        class="former-field-error"
      >
        <former-field-error
          :error="field.errors[0]"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import monetize from '@sdk/helpers/formatters/monetize';

  import FormerBoolean from '@/components/Former/Field/Boolean.vue';
  import FormerInput from '@/components/Former/Field/Input.vue';
  import FormerSelect from '@/components/Former/Field/Select.vue';
  import FormerSubmit from '@/components/Former/Field/Submit.vue';
  import FormerFieldError from '@/components/Former/Field/Error.vue';

  export default {
    name: 'FormerField',
    components: {
      FormerFieldError,
      FormerBoolean,
      FormerInput,
      FormerSelect,
      FormerSubmit,
    },
    props: {
      field: {
        type: Object,
        default() {
          return {};
        },
      },
    },
    data() {
      return {
        value: this.field.value,
        initialized: false,
      };
    },
    computed: {
      classes() {
        const cls = ['former-field-content'];

        if (this.boolean) {
          cls.push('former-field-content-transparent');
        }

        return cls;
      },
      submitClasses() {
        const cls = ['former-field-submit'];

        if (this.boolean) {
          cls.push('former-field-submit-round');
        }

        return cls;
      },
      type() {
        if (this.select) {
          return 'former-select';
        }

        if (this.boolean) {
          return 'former-boolean';
        }

        return 'former-input';
      },

      submitable() {
        if (this.required) {
          return !!this.value;
        }

        return true;
      },
      boolean() {
        return this.field.type === 'boolean';
      },
      select() {
        return this.field.type === 'select';
      },
      required() {
        let required = false;

        this.field.validations.some((validation) => {
          if (validation.type === 'blank') {
            required = true;
          }

          return required;
        });

        return required;
      },
      label() {
        const {
          boolean,
          field,
        } = this;

        return boolean ? (field.placeholder || field.label) : field.label;
      },
      button() {
        const zero = this.value === monetize(0, this.field.mask);
        const filled = this.required || (this.value && !zero);

        return {
          icon: filled ? '' : '',
          label: filled ? 'Salvar' : 'Pular',
          color: filled ? 'green' : 'red',
        };
      },
      inputClasses() {
        const classes = ['former-field-input'];

        if (this.field.errors.length) {
          classes.push('former-field-error');
        }

        return classes;
      },
    },
    watch: {
      'field.id': {
        handler() {
          this.$nextTick(() => {
            this.value = this.field.value;
            this.$nextTick(this.format);
          });
        },
      },
    },
    mounted() {
      if (this.field.editing) {
        this.format();
      }
    },
    methods: {
      format() {
        const {
          input,
        } = this.$refs;

        if (!input) {
          return;
        }

        if (input.focus) {
          input.focus(true);
        }

        if (input.resize) {
          input.resize();
        }
      },
      input() {
        if (this.select) {
          this.$refs.submit.focus();
          this.resized();
        }
      },
      submit() {
        return this.$emit('change', this.field.id, this.value);
      },
      resize() {
        const {
          input,
        } = this.$refs;

        if (input.resize) {
          input.resize();
        }
      },
      resized() {
        this.$emit('resize');
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/stylesheets/_variables.scss';

  .former-field {
  }

  .former-field-container {
    align-items: stretch;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .former-field-label {
    font-weight: $bold;
    text-align: center;
    margin-top: $gutter-small;
  }

  .former-field-content {
    align-items: stretch;
    background-color: $white;
    border-radius: $button * 0.5;
    display: flex;
    flex-direction: row;
    margin: $gutter-small;
    min-height: $button;
    justify-content: stretch;
  }

  .former-field-content-transparent {
    background-color: transparent;
  }

  .former-field-submit {
    border-bottom-left-radius: $radius;
    border-top-left-radius: $radius;
    font-size: $font-size;
    font-weight: $regular;
    height: auto !important;
    padding-left: $gutter;
    padding-right: $gutter + $gutter-smaller;
  }

  .former-field-submit-round {
    border-radius: $button * 0.5;
    margin-left: $gutter-small;
    padding-left: $gutter;
    padding-right: $gutter;
  }

  .former-field-element {
    flex: 1;
  }

  .former-field-error {
    align-self: center;
    align-items: center;
    background-color: $red;
    border-radius: $button-smaller * 0.5;
    color: $white;
    display: flex;
    justify-content: center;
    min-height: $button-smaller;
    padding: $gutter-smallest $gutter;
    margin: 0 $gutter-small $gutter-small;
    position: relative;
    text-align: center;
  }

  .former-field-required {
    color: $red;
    font-weight: $regular;
  }
</style>
